import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import {
  CustomerAnalyticsReport,
  CreditManagerArPerformanceReport,
  CreditManagerCreditPerformanceReport,
} from '@dill/dill-shared';

export const getSupplierReport = createAsyncThunk(
  'reports/getSupplierReport',
  async (
    {
      supplierId,
      year,
    }: {
      supplierId: string;
      year: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/reports/getSupplierReport`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            year,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getSupplierCustomerArReport = createAsyncThunk(
  'reports/getSupplierCustomerArReport',
  async (
    {
      supplierId,
    }: {
      supplierId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/reports/getSupplierCustomerArReport`,
        {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {},
        }
      );

      const file = new Blob([response.data], { type: 'application/xlsx' });
      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(file);
      link.download = `ar-report-${+new Date()}.xlsx`;
      link.click();

      return { success: true, data: { reportUrl: link.href } };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierCreditApplicationsReport = createAsyncThunk(
  'reports/getSupplierCreditApplicationsReport',
  async (
    {
      supplierId,
      year,
    }: {
      supplierId: string;
      year: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/reports/getSupplierCreditApplicationsReport`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            year,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCustomerAnalytics = createAsyncThunk(
  'reports/getCustomerAnalytics',
  async (
    {
      supplierId,
      year,
    }: {
      supplierId: string;
      year: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<{ success: boolean; data: CustomerAnalyticsReport }>(
        `${BASE_API_URL}/suppliers/${supplierId}/reports/getCustomerAnalytics`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            year,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditManagerArPerformance = createAsyncThunk(
  'reports/getCreditManagerArPerformance',
  async (
    {
      supplierId,
      days,
    }: {
      supplierId: string;
      days?: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<{
        success: boolean;
        data: CreditManagerArPerformanceReport;
      }>(`${BASE_API_URL}/suppliers/${supplierId}/reports/getCreditManagerArPerformance`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        params: days ? { days } : {},
      });

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export interface InvoiceCollectionNotesReportRow {
  buyerId: string;
  quickbooksCustomerId: string;
  businessName: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceAmount: number | null;
  dueDate: string | null;
  activitySummary: string;
  lastActivityDate: string | null;
  activityCount: number;
}

export const getSupplierInvoiceCollectionNotesReport = createAsyncThunk(
  'reports/getSupplierInvoiceCollectionNotesReport',
  async (
    {
      supplierId,
      startDate,
      endDate,
    }: {
      supplierId: string;
      startDate?: string;
      endDate?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<{
        success: boolean;
        data: InvoiceCollectionNotesReportRow[];
      }>(
        `${BASE_API_URL}/suppliers/${supplierId}/reports/getSupplierInvoiceCollectionNotesReport`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditReviewerPerformance = createAsyncThunk(
  'reports/getCreditReviewerPerformance',
  async (
    {
      supplierId,
      days,
    }: {
      supplierId: string;
      days?: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<{
        success: boolean;
        data: CreditManagerCreditPerformanceReport;
      }>(`${BASE_API_URL}/suppliers/${supplierId}/reports/getCreditReviewerPerformance`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        params: days ? { days } : {},
      });

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
